import { useEffect, useMemo, useState } from 'react';
import { PageHead } from '../../components/ui';
import { costSummaryText } from '../../lib/amCosts';
import { formatDate } from '../../lib/format';
import { parseIdList, type AmClientDetail, type AmSbr } from '../../lib/types';
import { SendPanel } from './amUi';

const APPROVED = ['Approved', 'In Progress', 'Completed'];

/**
 * The after-meeting follow-up: the recap email, with a copy of the
 * presentation itself to attach — the deck as it was presented (from the
 * review's snapshot), every slide on its own landscape page, saved as a PDF
 * from the print view (AmSbrPresentation's printAll). A mailto link cannot
 * carry an attachment, so the PDF is saved first and attached in the email.
 */
export function AmSbrFollowup({
  client,
  sbr,
  organizationName,
  onBack,
  onPresentationCopy,
}: {
  client: AmClientDetail;
  sbr: AmSbr;
  organizationName: string;
  onBack: () => void;
  /** Opens the presentation as a printable copy, every slide one to a page. */
  onPresentationCopy: () => void;
}) {
  const recIds = useMemo(() => parseIdList(sbr.RecIds), [sbr.RecIds]);
  const auditIds = useMemo(() => parseIdList(sbr.AuditIds), [sbr.AuditIds]);

  const recs = client.recommendations.filter((r) => recIds.includes(r.AmRecommendationId));
  const audits = client.audits.filter((a) => auditIds.includes(a.AmAuditId));

  const groups = useMemo(() => {
    const result = {
      approved: recs.filter((r) => APPROVED.includes(r.Status ?? '')),
      declined: recs.filter((r) => r.Status === 'Declined'),
      pending: recs.filter((r) => !APPROVED.includes(r.Status ?? '') && r.Status !== 'Declined'),
    };
    return result;
  }, [recs]);

  const [recipient, setRecipient] = useState('');
  useEffect(() => {
    const decisionMaker = client.stakeholders.find((s) => s.Email);
    setRecipient(decisionMaker?.Email ?? client.Email ?? '');
  }, [client]);

  const meetingDate = formatDate(sbr.NextDate);
  const decisionLabel = (status: string | null) =>
    APPROVED.includes(status ?? '') ? 'Approved' : status === 'Declined' ? 'Declined' : 'Pending';

  const emailBody = (() => {
    const lines = [
      `Dear ${client.PrimaryContact ?? client.Name},`,
      '',
      `Thank you for meeting with us for the ${sbr.Title ?? 'business review'} on ${meetingDate}. A copy of the presentation is attached, and below is a summary of the decisions made.`,
      '',
    ];
    if (audits.length > 0) {
      lines.push('REVIEWS COVERED');
      for (const audit of audits) lines.push(`• ${audit.Name} (${audit.SetName})`);
      lines.push('');
    }
    lines.push('RECOMMENDATIONS AND DECISIONS');
    recs.forEach((r, n) =>
      lines.push(
        `${n + 1}. ${r.Title} — ${decisionLabel(r.Status)} — ${costSummaryText(r)}`,
      ),
    );
    lines.push('');
    if (groups.approved.length > 0) {
      lines.push(
        'Approved items: we will proceed with scheduling and implementation of the approved recommendations.',
        '',
      );
    }
    if (groups.declined.length > 0) {
      lines.push('Declined items: please let us know if you would like to revisit any of these:');
      for (const r of groups.declined) lines.push(`   - ${r.Title}`);
      lines.push('');
    }
    lines.push(
      'Please let us know if anything above needs adjusting.',
      '',
      'Kind regards,',
      organizationName,
    );
    return lines.join('\n');
  })();

  return (
    <>
      <div className="no-print">
        <button type="button" className="btn btn-ghost btn-sm mb-4" onClick={onBack}>
          ← Back to the client
        </button>
        <PageHead
          title="Review follow-up"
          lede="Send the client a copy of the presentation, with a recap of the decisions captured in the meeting."
        />

        <div className="grid grid-3 mb-4">
          <div className="stat">
            <div className="label">Approved</div>
            <div className="figure">{groups.approved.length}</div>
            <div className="foot">proceeding to scheduling</div>
          </div>
          <div className="stat">
            <div className="label">Declined</div>
            <div className={`figure${groups.declined.length > 0 ? ' accent' : ''}`}>
              {groups.declined.length}
            </div>
            <div className="foot">{groups.declined.length > 0 ? 'listed in the email' : 'nothing declined'}</div>
          </div>
          <div className="stat">
            <div className="label">Still pending</div>
            <div className="figure">{groups.pending.length}</div>
            <div className="foot">no decision recorded</div>
          </div>
        </div>

        <section className="card mb-4 no-print">
          <div className="card-head">
            <div className="grow">
              <span className="eyebrow">Step 1</span>
              <h2>A copy of the presentation</h2>
              <p className="sub">
                Every slide as it was presented, one to a landscape page. Open it, choose <strong>Save as PDF</strong> in the print
                dialog, and attach the file to the email below.
              </p>
            </div>
            <button type="button" className="btn btn-primary" onClick={onPresentationCopy}>
              Open the presentation to save as PDF
            </button>
          </div>
        </section>

        <span className="eyebrow">Step 2</span>
        <SendPanel
          recipient={recipient}
          onRecipient={setRecipient}
          suggestions={[
            ...client.stakeholders
              .filter((s) => s.Email)
              .map((s) => ({
                value: s.Email as string,
                label: `${s.Name} (${s.Role ?? 'stakeholder'})`,
              })),
            ...(client.Email
              ? [{ value: client.Email, label: client.PrimaryContact ?? client.Name }]
              : []),
          ]}
          subject={`${sbr.Title ?? 'Business review'} follow-up — ${client.Name}${
            groups.declined.length > 0 ? ' — sign-off required' : ''
          }`}
          body={emailBody}
          disabled={recs.length === 0}
          copyLabel="Copy the email"
          copyText={() => emailBody}
        />
      </div>

    </>
  );
}
